/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2018. All Rights Reserved.
 #*
 #*===================================================================
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { FormGroup } from '@angular/forms';

@Component({
  selector: 'div[app-custom-type-ahead]',
  templateUrl: './custom-type-ahead.component.html',
  styleUrls: ['./custom-type-ahead.component.scss'],
})
export class CustomTypeAheadComponent implements AfterViewInit, OnDestroy, OnInit {
  @Input()
  parentModel: any = {};

  @Input()
  parentForm: FormGroup;

  @Input()
  propertyName: any;

  @Input()
  options: any = {};

  @Input()
  tabindex: any;

  @Input()
  defaultDescription?: string;

  @Input()
  readonly = false;

  @Input()
  allowClearance = false;

  @Input()
  customSelectBoxScrollableClassName?: string;

  @Input()
  allowNotSelectedValues?: string;

  @Input()
  allowNewValues?: boolean;

  @Output()
  onItemSelected: EventEmitter<any> = new EventEmitter();

  @Output()
  onInputValueChanged: EventEmitter<string> = new EventEmitter();

  autocompleteLoading: boolean;
  isAutocompleteOpen: boolean;
  autocompleteItems: any[];
  isItemSelected = false;

  @ViewChild('input') input: ElementRef;

  private componentDestroyed: Subject<any> = new Subject();

  constructor(private httpClient: HttpClient) {}

  ngOnInit() {}

  ngOnChanges() {
    if (
      this.parentForm.get(this.propertyName).value !== null &&
      this.parentForm.get(this.propertyName).value !== undefined
    ) {
      this.isItemSelected = true;
    } else {
      this.isItemSelected = false;
    }
  }

  ngAfterViewInit() {
    if (this.input) {
      setTimeout(() => {
        this.input.nativeElement.value = this.defaultDescription || '';
      });
      if (!this.readonly) {
        fromEvent(this.input.nativeElement, 'keyup')
          .pipe(
            map((ev: any) => ev.target.value),
            debounceTime(200),
            distinctUntilChanged(),
            takeUntil(this.componentDestroyed)
          )
          .subscribe((value: string) => {
            if (this.allowClearance && this.input.nativeElement.value === '') {
              this.onItemSelected.emit({ value: null, item: {} });
            } else {
              this.onAutocompleteTriggerEvent(value);
            }
            this.emitCurrentInputValue(value);
          });

        if (this.options && this.options.triggerOnFocus) {
          fromEvent(this.input.nativeElement, 'focus')
            .pipe(
              debounceTime(200),
              takeUntil(this.componentDestroyed)
            )
            .subscribe((ev: any) => {
              const value = ev.target.value;
              this.onAutocompleteTriggerEvent(value);
            });
        }
      }
    }
  }

  @HostListener('focus', ['$event']) onBlurInput(ev: any) {
    this.parentForm.get(this.propertyName).markAsDirty();
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
    this.componentDestroyed.complete();
  }

  onAutocompleteTriggerEvent(value: string) {
    this.autocompleteLoading = true;

    // if input is less than the minimum length to search then don't search
    if (this.options.minSearchLength && value.length < this.options.minSearchLength) {
      this.autocompleteLoading = false;
      this.isAutocompleteOpen = false;
      return;
    }

    // if items are provided then set those
    if (!this.options.url) {
      this.autocompleteItems = this.options.items.filter(
        item =>
          !value ||
          (item[this.options.selectedFieldDisplay] &&
            item[this.options.selectedFieldDisplay].toLowerCase().includes(value.toLowerCase()))
      );
      this.autocompleteLoading = false;
      this.isAutocompleteOpen = true;
      return;
    }

    this.getResults(value).subscribe(res => {
      this.autocompleteItems = res;
      if (res.length === 0) {
        this.clearSelectedValue(null, false);
      }
      this.autocompleteLoading = false;
      this.isAutocompleteOpen = !res.length && this.allowNotSelectedValues ? false : res.length !== 0;
      setTimeout(() => {
        document.getElementById(this.propertyName).focus();
      });
    });
  }

  onItemSelect(item: any) {
    this.parentModel[this.propertyName] = item[this.options.valueField];
    this.onItemSelected.emit({
      value: item[this.options.valueField],
      item,
    });
    this.input.nativeElement.value = this.options.selectedFieldDisplay
      ? item[this.options.selectedFieldDisplay]
      : item[this.options.textField];

    this.parentForm.get(this.propertyName).setValue(item[this.options.valueField]);
    this.isAutocompleteOpen = false;
    this.isItemSelected = true;
  }

  onEmptyItemSelect() {
    this.isAutocompleteOpen = false;
    this.parentForm.get(this.propertyName).setValue(null);
    this.onItemSelected.emit({ value: null, item: {} });
  }

  public emptyInput() {
    this.parentForm.get(this.propertyName).setValue(null);
    this.input.nativeElement.value = '';
    this.isItemSelected = false;
  }

  clearSelectedValue($event?, clearInput = true) {
    if ($event) {
      $event.preventDefault();
    }
    this.parentModel[this.propertyName] = null;
    this.parentForm.get(this.propertyName).setValue(null);
    this.isItemSelected = false;
    this.onEmptyItemSelect();

    if (clearInput) {
      this.input.nativeElement.value = '';
    }
  }

  closeMenu($event) {
    this.isAutocompleteOpen = false;
  }

  getResults(term: string): Observable<any[]> {
    const httpMethod = this.options.httpMethod || 'get';
    if (httpMethod === 'get') {
      let queryUrl = `${environment.apiHostname}${this.options.url}?`;
      if (this.options.queryParamName) {
        queryUrl += `${this.options.queryParamName}=${term}`;
      }

      return this.handleResponse(this.httpClient.get(queryUrl), this.options.formatterFunction, term);
    } else {
      const params = { ...this.options.defaultParams };
      params[this.options.queryParamName] = term;
      return this.handleResponse(
        this.httpClient.post(`${environment.apiHostname}${this.options.url}`, params),
        this.options.formatterFunction,
        term
      );
    }
  }

  getDescription(desc: string) {
    if (desc && desc.length > 40) {
      desc = desc.substr(0, 40) + '...';
    }
    return desc;
  }

  handleResponse(httpCall: Observable<any>, formatterFunction: any, term: string): Observable<any[]> {
    return httpCall.pipe(
      map((resp: any) => {
        if (resp.result.success) {
          return formatterFunction ? formatterFunction(resp.data, this.parentModel, term) : resp.data;
        } else {
          return [];
        }
      })
    );
  }

  isInputFilled(controlName) {
    if (this.parentForm) {
      const control = this.parentForm.get(controlName);
      return control && control.value;
    }
    return false;
  }

  isInputInvalid(controlName) {
    if (this.parentForm) {
      const control = this.parentForm.get(controlName);
      if (this.allowNewValues && this.input) {
        return control.dirty && this.input.nativeElement.value.length === 0;
      }
      return control && control.invalid && control.dirty && !this.isAutocompleteOpen;
    }
    return false;
  }

  private emitCurrentInputValue(value: string) {
    this.onInputValueChanged.emit(value);
  }
}
